import React, { useState, useEffect, useRef } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
  FaCalendarAlt,
  FaUser,
  FaBuilding,
  FaPercent,
  FaAngleDown,
  FaAngleUp,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import { MdBedroomParent } from 'react-icons/md';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL || 'http://localhost:9000';

const RoomReserve = ({ setDates }) => {
  const dispatch = useDispatch();

  // Get values from Redux store
  const { checkInDate, checkOutDate } = useSelector((state) => state.selectedDates);
  const { adults, children, rooms, childrenAges } = useSelector((state) => state.formDetails);

  // Get special rates from Redux
  const { specialRateType, corporateCode, seniorCitizenDiscount, promoCode, isSpecialRateApplied } =
    useSelector((state) => state.specialRates || {});

  const [showCalendar, setShowCalendar] = useState(false);
  const [showTravellersDropdown, setShowTravellersDropdown] = useState(false);
  const [showRoomsDropdown, setShowRoomsDropdown] = useState(false);
  const [showSpecialRateDropdown, setShowSpecialRateDropdown] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [localCorporateCode, setLocalCorporateCode] = useState(corporateCode || '');
  const [localPromoCode, setLocalPromoCode] = useState(promoCode || '');
  const [localSeniorDiscount, setLocalSeniorDiscount] = useState(seniorCitizenDiscount || false);
  const [showDetails, setShowDetails] = useState(false);

  // Contract validation states
  const [isValidatingContract, setIsValidatingContract] = useState(false);
  const [contractValidation, setContractValidation] = useState({
    isValid: null,
    message: '',
    data: null,
  });

  const calendarRef = useRef(null);
  const travellersRef = useRef(null);
  const roomsRef = useRef(null);
  const specialRateRef = useRef(null);

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const maxDate = new Date(today);
  maxDate.setFullYear(today.getFullYear() + 1);

  // Sync Redux values with local state
  useEffect(() => {
    if (checkInDate && checkOutDate) {
      setDateRange([new Date(checkInDate), new Date(checkOutDate)]);
    }
    setLocalCorporateCode(corporateCode || '');
    setLocalPromoCode(promoCode || '');
    setLocalSeniorDiscount(seniorCitizenDiscount || false);
  }, [checkInDate, checkOutDate, corporateCode, promoCode, seniorCitizenDiscount]);

  // Reset validation when corporate code changes
  useEffect(() => {
    if (contractValidation.isValid === true && !corporateCode) {
      setContractValidation({
        isValid: null,
        message: '',
        data: null,
      });
    }
  }, [corporateCode]);

  const handleDateChange = (range) => {
    if (range.length === 2) {
      const [start, end] = range;

      const checkinDate = new Date(
        Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())
      );
      const checkoutDate = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()));

      if (checkinDate.getTime() === checkoutDate.getTime()) {
        const nextDay = new Date(checkinDate);
        nextDay.setDate(nextDay.getDate() + 1);
        setDateRange([checkinDate, nextDay]);
        dispatch({
          type: 'SET_SELECTED_DATES',
          payload: [checkinDate, nextDay],
        });
        return;
      }

      const nights = calculateNights(checkinDate, checkoutDate);

      if (nights > 0) {
        setDateRange([checkinDate, checkoutDate]);
        dispatch({
          type: 'SET_SELECTED_DATES',
          payload: [checkinDate, checkoutDate],
        });
        setShowCalendar(false);
      } else {
        setDateRange([range[0], null]);
      }
    } else {
      setDateRange([range[0], null]);
    }
  };

  const handleCounterChange = (e, type, value) => {
    e.stopPropagation();
    if (value >= 0) {
      if (type === 'children') {
        dispatch({ type: 'SET_CHILDREN', payload: value });
        const newChildrenAges = [...childrenAges];
        if (value > childrenAges.length) {
          while (newChildrenAges.length < value) {
            newChildrenAges.push(5);
          }
        } else {
          newChildrenAges.length = value;
        }
        dispatch({ type: 'SET_CHILDREN_AGES', payload: newChildrenAges });
      } else {
        dispatch({ type: `SET_${type.toUpperCase()}`, payload: value });
      }
    }
  };

  const handleAgeChange = (index, value) => {
    const updatedAges = [...childrenAges];
    updatedAges[index] = parseInt(value);
    dispatch({ type: 'SET_CHILDREN_AGES', payload: updatedAges });
  };

  // Validate contract API call
  const validateContract = async (contractId) => {
    if (!contractId || contractId.trim() === '') {
      setContractValidation({
        isValid: false,
        message: 'Please enter a contract ID',
        data: null,
      });
      return false;
    }

    setIsValidatingContract(true);
    setContractValidation({
      isValid: null,
      message: 'Validating contract...',
      data: null,
    });

    try {
      console.log(`Validating contract: ${contractId}`);
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/guest/contracts/${encodeURIComponent(contractId)}/validate`
      );

      const validationResult = await response.json();
      console.log('Contract validation result:', validationResult);

      if (validationResult.status === 'valid') {
        setContractValidation({
          isValid: true,
          message: validationResult.message,
          data: validationResult,
        });
        return true;
      } else {
        setContractValidation({
          isValid: false,
          message: validationResult.message,
          data: validationResult,
        });
        return false;
      }
    } catch (error) {
      console.error('Contract validation error:', error);
      setContractValidation({
        isValid: false,
        message: 'Failed to validate contract. Please try again.',
        data: null,
      });
      return false;
    } finally {
      setIsValidatingContract(false);
    }
  };

  // Handle special rate selection
  const handleSpecialRateSelect = (rateType) => {
    dispatch({ type: 'SET_SPECIAL_RATE_TYPE', payload: rateType });
    setShowDetails(true);
  };

  const handleApplyCorporateCode = async () => {
    if (!localCorporateCode.trim()) {
      setContractValidation({
        isValid: false,
        message: 'Please enter a contract ID',
        data: null,
      });
      return;
    }

    // If senior citizen discount is already applied, clear it first
    if (localSeniorDiscount) {
      setLocalSeniorDiscount(false);
      dispatch({ type: 'SET_SENIOR_CITIZEN_DISCOUNT', payload: false });
    }

    // Validate contract before applying
    const isValid = await validateContract(localCorporateCode.trim());

    if (isValid) {
      dispatch({ type: 'SET_CORPORATE_CODE', payload: localCorporateCode.trim() });
      dispatch({ type: 'SET_SPECIAL_RATE_APPLIED', payload: true });
      setShowSpecialRateDropdown(false);
      setShowDetails(true);
    }
  };

  const handleSeniorCitizenToggle = () => {
    const newValue = !localSeniorDiscount;

    // If corporate code is already applied, clear it first
    if (newValue && localCorporateCode.trim()) {
      // Clear corporate code
      setLocalCorporateCode('');
      dispatch({ type: 'SET_CORPORATE_CODE', payload: '' });
      // Clear contract validation
      setContractValidation({
        isValid: null,
        message: '',
        data: null,
      });
    }

    setLocalSeniorDiscount(newValue);
    dispatch({ type: 'SET_SENIOR_CITIZEN_DISCOUNT', payload: newValue });
    dispatch({ type: 'SET_SPECIAL_RATE_APPLIED', payload: newValue || corporateCode || promoCode });

    if (newValue) {
      setShowDetails(true);
    }
  };

  const handleClearSpecialRates = () => {
    dispatch({ type: 'CLEAR_SPECIAL_RATES' });
    setLocalCorporateCode('');
    setLocalPromoCode('');
    setLocalSeniorDiscount(false);
    setShowDetails(false);
    setShowSpecialRateDropdown(false);
    setContractValidation({
      isValid: null,
      message: '',
      data: null,
    });
  };

  // Get special rate display text
  const getSpecialRateDisplayText = () => {
    if (isSpecialRateApplied) {
      // Check for senior discount
      const hasSenior = seniorCitizenDiscount;
      // Check for corporate discount
      const hasCorporate = corporateCode && corporateCode.trim() !== '';

      // If senior discount is applied
      if (hasSenior) {
        return 'Senior Discount';
      }

      // If corporate discount is applied
      if (hasCorporate) {
        return `Corporate: ${corporateCode}`;
      }

      // If promo code (though typically not combined with others)
      if (promoCode && promoCode.trim() !== '') {
        return `Promo: ${promoCode}`;
      }
    }
    return 'Select Special Rate';
  };

  const calculateNights = (startDate, endDate) => {
    if (startDate && endDate) {
      const timeDiff = endDate.getTime() - startDate.getTime();
      const nights = Math.floor(timeDiff / (1000 * 3600 * 24));
      return nights > 0 ? nights : 1;
    }
    return 0;
  };

  // Check if any special rate is applied
  const isAnyRateApplied =
    isSpecialRateApplied && (seniorCitizenDiscount || corporateCode || promoCode);

  // Check if corporate code is applied
  const isCorporateCodeApplied = corporateCode && corporateCode.trim() !== '';

  // Check if senior citizen discount is applied
  const isSeniorCitizenApplied = seniorCitizenDiscount;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCalendar && calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
      if (
        showTravellersDropdown &&
        travellersRef.current &&
        !travellersRef.current.contains(event.target)
      ) {
        setShowTravellersDropdown(false);
      }
      if (showRoomsDropdown && roomsRef.current && !roomsRef.current.contains(event.target)) {
        setShowRoomsDropdown(false);
      }
      if (
        showSpecialRateDropdown &&
        specialRateRef.current &&
        !specialRateRef.current.contains(event.target)
      ) {
        setShowSpecialRateDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar, showTravellersDropdown, showRoomsDropdown, showSpecialRateDropdown]);

  return (
    <div className="mt-6">
      <div className="px-5">
        <h1 className="text-2xl text-black font-bold mb-1">Select Your Room Page</h1>
        <p className="">Enter your stay details and reserve your preferred room</p>
      </div>

      {/* Contract Validation Error Message */}
      {contractValidation.isValid === false && (
        <div className="px-5 mt-2">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r">
            <div className="flex">
              <div className="flex-shrink-0">
                <FaExclamationTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-semibold text-red-700">{contractValidation.message}</p>
                {contractValidation.data?.action && (
                  <p className="text-xs text-red-600 mt-1">{contractValidation.data.action}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contract Validation Success Message */}
      {contractValidation.isValid === true && (
        <div className="px-5 mt-2">
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r">
            <div className="flex">
              <div className="flex-shrink-0">
                <FaCheck className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-semibold text-green-700">{contractValidation.message}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Four Boxes in One Row */}
      <div className="px-5 rounded-lg mt-4 relative flex gap-2">
        {/* Date Selection */}
        <div
          className="flex flex-col border border-gray-700 p-3 rounded-md relative cursor-pointer w-2/6"
          onClick={() => {
            setShowCalendar(true);
            setShowTravellersDropdown(false);
            setShowRoomsDropdown(false);
            setShowSpecialRateDropdown(false);
          }}
        >
          <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
            Check-in & Check-Out Dates
          </label>
          <div className="flex items-center space-x-3">
            <FaCalendarAlt size={20} className="text-gray-800 md:w-4 sm:w-3" />
            <span className="text-gray-800 font-poppins text-base md:text-sm sm:text-xs font-semibold">
              {dateRange[0] && dateRange[1]
                ? `${dateRange[0].toDateString()} - ${dateRange[1].toDateString()}`
                : 'Select Dates'}
            </span>
            <div className="absolute top-2 right-2 py-1 px-2 rounded-md text-xs md:text-xs sm:text-xxs text-white font-semibold bg-black">
              {dateRange[0] && dateRange[1]
                ? `(${calculateNights(dateRange[0], dateRange[1])} ${
                    calculateNights(dateRange[0], dateRange[1]) === 1 ? 'Night' : 'Nights'
                  })`
                : ''}
            </div>
          </div>
          {showCalendar && (
            <div
              ref={calendarRef}
              className="absolute top-20 left-0 bg-white shadow-lg border rounded-md p-4 z-10 md:top-20 sm:top-14"
            >
              <Calendar
                onChange={handleDateChange}
                value={dateRange}
                selectRange={true}
                className="react-calendar p-2 mt-4 bg-white w-full sm:w-3/4 lg:w-1/2 md:1/3"
                minDate={today}
                maxDate={maxDate}
                prev2Label={null}
                next2Label={null}
                minDetail="year"
              />
            </div>
          )}
        </div>

        {/* Travellers Selection */}
        <div
          ref={travellersRef}
          className="flex flex-col border border-gray-800 p-3 rounded-md relative cursor-pointer w-1/4"
          onClick={() => {
            setShowTravellersDropdown(!showTravellersDropdown);
            setShowCalendar(false);
            setShowRoomsDropdown(false);
            setShowSpecialRateDropdown(false);
          }}
        >
          <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
            Guests
          </label>
          <div className="flex items-center space-x-2">
            <FaUser className="text-gray-800" />
            <span className="text-gray-800 text-base md:text-sm sm:text-xs font-poppins font-semibold">
              {adults} Adults{children > 0 ? `, ${children} Children` : ''}
            </span>
          </div>
          {showTravellersDropdown && (
            <div
              className="absolute top-20 left-0 bg-white shadow-lg font-poppins border rounded-md p-4 z-10 w-80"
              onClick={(e) => e.stopPropagation()}
            >
              {['adults', 'children'].map((type) => {
                const value = { adults, children }[type];
                const minValue = type === 'adults' ? 1 : 0;

                return (
                  <div key={type} className="flex justify-between items-center py-2">
                    <span className="text-black capitalize font-semibold">{type}</span>
                    <div className="flex items-center space-x-4">
                      <button
                        className={`px-4 py-2 rounded-full transition-colors ${
                          value <= minValue
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                        onClick={(e) => value > minValue && handleCounterChange(e, type, value - 1)}
                        disabled={value <= minValue}
                      >
                        -
                      </button>
                      <span className="text-lg font-semibold">{value}</span>
                      <button
                        className="px-3.5 py-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                        onClick={(e) => handleCounterChange(e, type, value + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}

              {children > 0 && (
                <div className="mt-1">
                  <p className="text-gray-500 text-sm mb-3">
                    You can add children below 5 years old
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {childrenAges.map((age, index) => (
                      <div key={index} className="flex flex-col">
                        <label className="text-sm text-gray-700 mb-1">Child {index + 1} Age</label>
                        <select
                          value={age}
                          onChange={(e) => handleAgeChange(index, e.target.value)}
                          className="border border-gray-300 rounded-md p-2"
                        >
                          {Array.from({ length: 5 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1} {i === 0 ? 'year' : 'years'} old
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                className="w-full bg-black text-white px-4 py-2 mt-3 rounded-md font-poppins hover:text-white transition"
                onClick={() => setShowTravellersDropdown(false)}
              >
                Done
              </button>
            </div>
          )}
        </div>

        {/* Special Rate Section */}
        <div
          ref={specialRateRef}
          className="flex flex-col border border-gray-800 p-3 rounded-md relative cursor-pointer w-1/5"
          onClick={() => {
            setShowSpecialRateDropdown(!showSpecialRateDropdown);
            setShowCalendar(false);
            setShowTravellersDropdown(false);
            setShowRoomsDropdown(false);
          }}
        >
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => setShowDetails(!showDetails)}
          >
            <div className="flex flex-col">
              <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
                Special Rate
              </label>

              <div className="flex items-center space-x-1">
                <FaPercent className="text-gray-800" size={12} />
                <span className="text-gray-800 text-xs md:text-xs sm:text-xs font-poppins font-semibold">
                  {getSpecialRateDisplayText()}
                </span>
              </div>
            </div>

            <div className="flex items-center">{showDetails ? <FaAngleUp /> : <FaAngleDown />}</div>
          </div>

          {showSpecialRateDropdown && (
            <div
              className="absolute top-20 left-0 bg-white shadow-lg border rounded-md p-4 z-10 w-76"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4">
                <h3 className="text-sm font-bold text-gray-800 mb-3">Select Special Rate</h3>

                {/* Senior Citizen Discount - Disabled if corporate code is already applied */}
                {/* <div className={`mb-4 p-3 border border-gray-200 rounded-md ${isCorporateCodeApplied ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <MdElderly className={`text-xl ${isCorporateCodeApplied ? 'text-gray-400' : 'text-blue-600'}`} />
                      <div>
                        <label className={`font-semibold text-sm cursor-pointer ${isCorporateCodeApplied ? 'text-gray-500' : 'text-gray-700'}`}>
                          Senior Citizen Discount
                        </label>
                        <p className="text-xs text-gray-500">
                          Apply senior citizen discount
                          {isCorporateCodeApplied && (
                            <span className="text-red-500 ml-1">(Clear corporate code first)</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={localSeniorDiscount}
                        onChange={handleSeniorCitizenToggle}
                        disabled={isCorporateCodeApplied}
                        className={`h-5 w-5 rounded cursor-pointer ${isCorporateCodeApplied ? 'bg-gray-200 cursor-not-allowed' : 'text-blue-600'}`}
                      />
                    </div>
                  </div>
                </div> */}

                {/* Corporate Code - Input Field - Disabled if senior citizen discount is applied */}
                <div
                  className={`mb-4 p-3 border border-gray-200 rounded-md ${isSeniorCitizenApplied ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                >
                  <div className="flex items-start space-x-3">
                    <FaBuilding
                      className={`mt-1 text-xl ${isSeniorCitizenApplied ? 'text-gray-400' : 'text-green-600'}`}
                    />
                    <div className="flex-1">
                      <label
                        className={`font-semibold text-gray-700 mb-1 block text-sm ${isSeniorCitizenApplied ? 'text-gray-500' : 'text-gray-700'}`}
                      >
                        Special code
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={localCorporateCode}
                          onChange={(e) => {
                            setLocalCorporateCode(e.target.value);
                            // Clear validation when user types
                            if (contractValidation.isValid !== null) {
                              setContractValidation({
                                isValid: null,
                                message: '',
                                data: null,
                              });
                            }
                          }}
                          placeholder="Enter special code"
                          className={`flex-1 border border-gray-300 rounded-md py-1 px-2 text-sm ${isSeniorCitizenApplied || isValidatingContract || isCorporateCodeApplied ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          disabled={
                            isSeniorCitizenApplied || isValidatingContract || isCorporateCodeApplied
                          }
                        />
                        <button
                          onClick={handleApplyCorporateCode}
                          disabled={
                            !localCorporateCode.trim() ||
                            isValidatingContract ||
                            isSeniorCitizenApplied ||
                            isCorporateCodeApplied
                          }
                          className={`px-4 py-2 rounded-md text-sm transition ${
                            localCorporateCode.trim() &&
                            !isValidatingContract &&
                            !isSeniorCitizenApplied &&
                            !isCorporateCodeApplied
                              ? 'bg-black text-white hover:bg-gray-900'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          {isValidatingContract ? 'Validating...' : 'Apply'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Enter your hotel code for corporate rates
                        {isSeniorCitizenApplied && (
                          <span className="text-red-500 ml-1">(Clear senior discount first)</span>
                        )}
                      </p>

                      {/* Validation status indicator */}
                      {contractValidation.isValid !== null && (
                        <div className="mt-2 text-xs">
                          {contractValidation.isValid === true ? (
                            <span className="text-green-600 flex items-center">
                              <FaCheck className="mr-1" /> Contract code is valid
                            </span>
                          ) : contractValidation.isValid === false ? (
                            <span className="text-red-600 flex items-center">
                              <FaTimes className="mr-1" /> Invalid contract code
                            </span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Clear Rate Button - Only show if a rate is applied */}
                {isAnyRateApplied && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <button
                      onClick={handleClearSpecialRates}
                      className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm hover:bg-gray-300 transition"
                    >
                      Clear Special Rate
                    </button>
                  </div>
                )}

                <button
                  className="w-full bg-black text-white px-4 py-2 mt-2 rounded-md text-sm font-poppins hover:bg-gray-800 transition"
                  onClick={() => setShowSpecialRateDropdown(false)}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Room Selection */}
        <div
          ref={roomsRef}
          className="flex flex-col border border-gray-800 p-3 rounded-md relative cursor-pointer w-1/5"
          onClick={() => {
            setShowRoomsDropdown(!showRoomsDropdown);
            setShowCalendar(false);
            setShowTravellersDropdown(false);
            setShowSpecialRateDropdown(false);
          }}
        >
          <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
            Rooms
          </label>
          <div className="flex items-center space-x-2">
            <MdBedroomParent size={24} className="text-gray-800" />
            <span className="text-gray-800 text-base md:text-sm sm:text-xs font-poppins font-semibold">
              {rooms} Room(s)
            </span>
          </div>
          {showRoomsDropdown && (
            <div className="absolute top-20 left-0 bg-white shadow-lg border rounded-md p-4 z-10 w-76">
              <div className="flex justify-between items-center py-2">
                <span className="text-black font-poppins capitalize font-semibold">Rooms</span>
                <div className="flex items-center space-x-4">
                  <button
                    className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                    onClick={(e) => handleCounterChange(e, 'rooms', Math.max(1, rooms - 1))}
                  >
                    -
                  </button>
                  <span className="text-lg font-semibold">{rooms}</span>
                  <button
                    className={`px-3.5 py-2 rounded-full transition-colors ${
                      rooms >= 1
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                    onClick={(e) => {
                      if (rooms < 1) {
                        handleCounterChange(e, 'rooms', rooms + 1);
                      }
                    }}
                    disabled={rooms >= 1}
                  >
                    +
                  </button>
                </div>
              </div>

              <p className="text-xs text-gray-900 mt-2 bg-yellow-200 px-2 py-1 rounded-sm">
                Reservations are limited to one room each. To reserve multiple rooms, please create
                separate reservations
              </p>
              <button
                className="w-full bg-black text-white px-4 py-2 mt-3 font-poppins rounded-md hover:text-white transition"
                onClick={() => setShowRoomsDropdown(false)}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoomReserve;

// import React, { useState, useEffect, useRef } from "react";
// import Calendar from "react-calendar";
// import "react-calendar/dist/Calendar.css";
// import { FaCalendarAlt, FaUser, FaBuilding, FaPercent, FaAngleDown, FaAngleUp, FaCheck, FaTimes, FaExclamationTriangle } from "react-icons/fa";
// import { MdElderly } from "react-icons/md";
// import { useSelector, useDispatch } from "react-redux";
// import { MdBedroomParent } from "react-icons/md";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL || "http://localhost:9000";

// const RoomReserve = ({ setDates }) => {
//   const dispatch = useDispatch();

//   // Get values from Redux store
//   const { checkInDate, checkOutDate } = useSelector(
//     (state) => state.selectedDates
//   );
//   const { adults, children, rooms, childrenAges } = useSelector(
//     (state) => state.formDetails
//   );

//   // Get special rates from Redux
//   const {
//     specialRateType,
//     corporateCode,
//     seniorCitizenDiscount,
//     promoCode,
//     isSpecialRateApplied
//   } = useSelector((state) => state.specialRates || {});

//   const [showCalendar, setShowCalendar] = useState(false);
//   const [showTravellersDropdown, setShowTravellersDropdown] = useState(false);
//   const [showRoomsDropdown, setShowRoomsDropdown] = useState(false);
//   const [showSpecialRateDropdown, setShowSpecialRateDropdown] = useState(false);
//   const [dateRange, setDateRange] = useState([null, null]);
//   const [localCorporateCode, setLocalCorporateCode] = useState(corporateCode || "");
//   const [localPromoCode, setLocalPromoCode] = useState(promoCode || "");
//   const [localSeniorDiscount, setLocalSeniorDiscount] = useState(seniorCitizenDiscount || false);
//   const [showDetails, setShowDetails] = useState(false);

//   // Contract validation states
//   const [isValidatingContract, setIsValidatingContract] = useState(false);
//   const [contractValidation, setContractValidation] = useState({
//     isValid: null,
//     message: "",
//     data: null
//   });

//   const calendarRef = useRef(null);
//   const travellersRef = useRef(null);
//   const roomsRef = useRef(null);
//   const specialRateRef = useRef(null);

//   const today = new Date();
//   const tomorrow = new Date(today);
//   tomorrow.setDate(today.getDate() + 1);
//   const maxDate = new Date(today);
//   maxDate.setFullYear(today.getFullYear() + 1);

//   // Sync Redux values with local state
//   useEffect(() => {
//     if (checkInDate && checkOutDate) {
//       setDateRange([new Date(checkInDate), new Date(checkOutDate)]);
//     }
//     setLocalCorporateCode(corporateCode || "");
//     setLocalPromoCode(promoCode || "");
//     setLocalSeniorDiscount(seniorCitizenDiscount || false);
//   }, [checkInDate, checkOutDate, corporateCode, promoCode, seniorCitizenDiscount]);

//   // Reset validation when corporate code changes
//   useEffect(() => {
//     if (contractValidation.isValid === true && !corporateCode) {
//       setContractValidation({
//         isValid: null,
//         message: "",
//         data: null
//       });
//     }
//   }, [corporateCode]);

//   const handleDateChange = (range) => {
//     if (range.length === 2) {
//       const [start, end] = range;

//       const checkinDate = new Date(
//         Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())
//       );
//       const checkoutDate = new Date(
//         Date.UTC(end.getFullYear(), end.getMonth(), end.getDate())
//       );

//       if (checkinDate.getTime() === checkoutDate.getTime()) {
//         const nextDay = new Date(checkinDate);
//         nextDay.setDate(nextDay.getDate() + 1);
//         setDateRange([checkinDate, nextDay]);
//         dispatch({
//           type: "SET_SELECTED_DATES",
//           payload: [checkinDate, nextDay],
//         });
//         return;
//       }

//       const nights = calculateNights(checkinDate, checkoutDate);

//       if (nights > 0) {
//         setDateRange([checkinDate, checkoutDate]);
//         dispatch({
//           type: "SET_SELECTED_DATES",
//           payload: [checkinDate, checkoutDate],
//         });
//         setShowCalendar(false);
//       } else {
//         setDateRange([range[0], null]);
//       }
//     } else {
//       setDateRange([range[0], null]);
//     }
//   };

//   const handleCounterChange = (e, type, value) => {
//     e.stopPropagation();
//     if (value >= 0) {
//       if (type === "children") {
//         dispatch({ type: "SET_CHILDREN", payload: value });
//         const newChildrenAges = [...childrenAges];
//         if (value > childrenAges.length) {
//           while (newChildrenAges.length < value) {
//             newChildrenAges.push(5);
//           }
//         } else {
//           newChildrenAges.length = value;
//         }
//         dispatch({ type: "SET_CHILDREN_AGES", payload: newChildrenAges });
//       } else {
//         dispatch({ type: `SET_${type.toUpperCase()}`, payload: value });
//       }
//     }
//   };

//   const handleAgeChange = (index, value) => {
//     const updatedAges = [...childrenAges];
//     updatedAges[index] = parseInt(value);
//     dispatch({ type: "SET_CHILDREN_AGES", payload: updatedAges });
//   };

//   // Validate contract API call
//   const validateContract = async (contractId) => {
//     if (!contractId || contractId.trim() === "") {
//       setContractValidation({
//         isValid: false,
//         message: "Please enter a contract ID",
//         data: null
//       });
//       return false;
//     }

//     setIsValidatingContract(true);
//     setContractValidation({
//       isValid: null,
//       message: "Validating contract...",
//       data: null
//     });

//     try {
//       console.log(`Validating contract: ${contractId}`);
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/guest/contracts/${encodeURIComponent(contractId)}/validate`
//       );

//       const validationResult = await response.json();
//       console.log("Contract validation result:", validationResult);

//       if (validationResult.status === "valid") {
//         setContractValidation({
//           isValid: true,
//           message: validationResult.message,
//           data: validationResult
//         });
//         return true;
//       } else {
//         setContractValidation({
//           isValid: false,
//           message: validationResult.message,
//           data: validationResult
//         });
//         return false;
//       }
//     } catch (error) {
//       console.error("Contract validation error:", error);
//       setContractValidation({
//         isValid: false,
//         message: "Failed to validate contract. Please try again.",
//         data: null
//       });
//       return false;
//     } finally {
//       setIsValidatingContract(false);
//     }
//   };

//   // Handle special rate selection
//   const handleSpecialRateSelect = (rateType) => {
//     dispatch({ type: "SET_SPECIAL_RATE_TYPE", payload: rateType });
//     setShowDetails(true);
//   };

//   const handleApplyCorporateCode = async () => {
//     if (!localCorporateCode.trim()) {
//       setContractValidation({
//         isValid: false,
//         message: "Please enter a contract ID",
//         data: null
//       });
//       return;
//     }

//     // Validate contract before applying
//     const isValid = await validateContract(localCorporateCode.trim());

//     if (isValid) {
//       dispatch({ type: "SET_CORPORATE_CODE", payload: localCorporateCode.trim() });
//       dispatch({ type: "SET_SPECIAL_RATE_APPLIED", payload: true });
//       setShowSpecialRateDropdown(false);
//       setShowDetails(true);
//     }
//   };

//   // Commenting out promo code for now as requested
//   /*
//   const handleApplyPromoCode = () => {
//     if (localPromoCode.trim()) {
//       dispatch({ type: "SET_PROMO_CODE", payload: localPromoCode.trim() });
//       dispatch({ type: "SET_SPECIAL_RATE_APPLIED", payload: true });
//       setShowSpecialRateDropdown(false);
//       setShowDetails(true);
//     }
//   };
//   */

//   const handleSeniorCitizenToggle = () => {
//     const newValue = !localSeniorDiscount;
//     setLocalSeniorDiscount(newValue);
//     dispatch({ type: "SET_SENIOR_CITIZEN_DISCOUNT", payload: newValue });
//     dispatch({ type: "SET_SPECIAL_RATE_APPLIED", payload: newValue || corporateCode || promoCode });
//     if (newValue) {
//       setShowDetails(true);
//     }
//   };

//   const handleClearSpecialRates = () => {
//     dispatch({ type: "CLEAR_SPECIAL_RATES" });
//     setLocalCorporateCode("");
//     setLocalPromoCode("");
//     setLocalSeniorDiscount(false);
//     setShowDetails(false);
//     setShowSpecialRateDropdown(false);
//     setContractValidation({
//       isValid: null,
//       message: "",
//       data: null
//     });
//   };

//   // Get special rate display text
//   const getSpecialRateDisplayText = () => {
//     if (isSpecialRateApplied) {
//       const rates = [];
//       if (seniorCitizenDiscount) rates.push("Senior Discount");
//       if (corporateCode) rates.push(`Corporate: ${corporateCode}`);
//       if (promoCode) rates.push(`Promo: ${promoCode}`);

//       if (rates.length > 0) {
//         return rates.join(", ");
//       }
//     }
//     return "Select Special Rate";
//   };

//   const calculateNights = (startDate, endDate) => {
//     if (startDate && endDate) {
//       const timeDiff = endDate.getTime() - startDate.getTime();
//       const nights = Math.floor(timeDiff / (1000 * 3600 * 24));
//       return nights > 0 ? nights : 1;
//     }
//     return 0;
//   };

//   // Check if any special rate is applied
//   const isAnyRateApplied = isSpecialRateApplied && (seniorCitizenDiscount || corporateCode || promoCode);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (
//         showCalendar &&
//         calendarRef.current &&
//         !calendarRef.current.contains(event.target)
//       ) {
//         setShowCalendar(false);
//       }
//       if (
//         showTravellersDropdown &&
//         travellersRef.current &&
//         !travellersRef.current.contains(event.target)
//       ) {
//         setShowTravellersDropdown(false);
//       }
//       if (
//         showRoomsDropdown &&
//         roomsRef.current &&
//         !roomsRef.current.contains(event.target)
//       ) {
//         setShowRoomsDropdown(false);
//       }
//       if (
//         showSpecialRateDropdown &&
//         specialRateRef.current &&
//         !specialRateRef.current.contains(event.target)
//       ) {
//         setShowSpecialRateDropdown(false);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, [showCalendar, showTravellersDropdown, showRoomsDropdown, showSpecialRateDropdown]);

//   return (
//     <div className="mt-6">
//       <div className="px-5">
//         <h1 className="text-2xl text-black font-bold mb-1">
//           Select Your Room Page
//         </h1>
//         <p className="">
//           Enter your stay details and reserve your preferred room
//         </p>
//       </div>

//       {/* Contract Validation Error Message */}
//       {contractValidation.isValid === false && (
//         <div className="px-5 mt-2">
//           <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r">
//             <div className="flex">
//               <div className="flex-shrink-0">
//                 <FaExclamationTriangle className="h-5 w-5 text-red-400" />
//               </div>
//               <div className="ml-3">
//                 <p className="text-sm font-semibold text-red-700">
//                    {contractValidation.message}
//                 </p>
//                 {contractValidation.data?.action && (
//                   <p className="text-xs text-red-600 mt-1">
//                     {contractValidation.data.action}
//                   </p>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Contract Validation Success Message */}
//       {contractValidation.isValid === true && (
//         <div className="px-5 mt-2">
//           <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r">
//             <div className="flex">
//               <div className="flex-shrink-0">
//                 <FaCheck className="h-5 w-5 text-green-400" />
//               </div>
//               <div className="ml-3">
//                 <p className="text-sm font-semibold text-green-700">
//                    {contractValidation.message}
//                 </p>
//                 {contractValidation.data?.companyName && (
//                   <p className="text-xs text-green-600 mt-1">
//                     Company name: {contractValidation.data.companyName}
//                   </p>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* All Four Boxes in One Row */}
//       <div className="px-5 rounded-lg mt-4 relative flex gap-2">
//         {/* Date Selection */}
//         <div
//           className="flex flex-col border border-gray-700 p-3 rounded-md relative cursor-pointer w-2/6"
//           onClick={() => {
//             setShowCalendar(true);
//             setShowTravellersDropdown(false);
//             setShowRoomsDropdown(false);
//             setShowSpecialRateDropdown(false);
//           }}
//         >
//           <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
//             Check-in & Check-Out Dates
//           </label>
//           <div className="flex items-center space-x-3">
//             <FaCalendarAlt size={20} className="text-gray-800 md:w-4 sm:w-3" />
//             <span className="text-gray-800 font-poppins text-base md:text-sm sm:text-xs font-semibold">
//               {dateRange[0] && dateRange[1]
//                 ? `${dateRange[0].toDateString()} - ${dateRange[1].toDateString()}`
//                 : "Select Dates"}
//             </span>
//             <div className="absolute top-2 right-2 py-1 px-2 rounded-md text-xs md:text-xs sm:text-xxs text-white font-semibold bg-black">
//               {dateRange[0] && dateRange[1]
//                 ? `(${calculateNights(dateRange[0], dateRange[1])} ${calculateNights(dateRange[0], dateRange[1]) === 1
//                   ? "Night"
//                   : "Nights"
//                 })`
//                 : ""}
//             </div>
//           </div>
//           {showCalendar && (
//             <div
//               ref={calendarRef}
//               className="absolute top-20 left-0 bg-white shadow-lg border rounded-md p-4 z-10 md:top-20 sm:top-14"
//             >
//               <Calendar
//                 onChange={handleDateChange}
//                 value={dateRange}
//                 selectRange={true}
//                 className="react-calendar p-2 mt-4 bg-white w-full sm:w-3/4 lg:w-1/2 md:1/3"
//                 minDate={today}
//                 maxDate={maxDate}
//                 prev2Label={null}
//                 next2Label={null}
//                 minDetail="year"
//               />
//             </div>
//           )}
//         </div>

//         {/* Travellers Selection */}
//         <div
//           ref={travellersRef}
//           className="flex flex-col border border-gray-800 p-3 rounded-md relative cursor-pointer w-1/4"
//           onClick={() => {
//             setShowTravellersDropdown(!showTravellersDropdown);
//             setShowCalendar(false);
//             setShowRoomsDropdown(false);
//             setShowSpecialRateDropdown(false);
//           }}
//         >
//           <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
//             Guests
//           </label>
//           <div className="flex items-center space-x-2">
//             <FaUser className="text-gray-800" />
//             <span className="text-gray-800 text-base md:text-sm sm:text-xs font-poppins font-semibold">
//               {adults} Adults{children > 0 ? `, ${children} Children` : ""}
//             </span>
//           </div>
//           {showTravellersDropdown && (
//             <div
//               className="absolute top-20 left-0 bg-white shadow-lg font-poppins border rounded-md p-4 z-10 w-80"
//               onClick={(e) => e.stopPropagation()}
//             >
//               {["adults", "children"].map((type) => {
//                 const value = { adults, children }[type];
//                 const minValue = type === "adults" ? 1 : 0;

//                 return (
//                   <div
//                     key={type}
//                     className="flex justify-between items-center py-2"
//                   >
//                     <span className="text-black capitalize font-semibold">
//                       {type}
//                     </span>
//                     <div className="flex items-center space-x-4">
//                       <button
//                         className={`px-4 py-2 rounded-full transition-colors ${value <= minValue
//                           ? "bg-gray-200 text-gray-400 cursor-not-allowed"
//                           : "bg-blue-100 text-blue-700 hover:bg-blue-200"
//                           }`}
//                         onClick={(e) =>
//                           value > minValue &&
//                           handleCounterChange(e, type, value - 1)
//                         }
//                         disabled={value <= minValue}
//                       >
//                         -
//                       </button>
//                       <span className="text-lg font-semibold">{value}</span>
//                       <button
//                         className="px-3.5 py-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
//                         onClick={(e) => handleCounterChange(e, type, value + 1)}
//                       >
//                         +
//                       </button>
//                     </div>
//                   </div>
//                 );
//               })}

//               {children > 0 && (
//                 <div className="mt-1">
//                   <p className="text-gray-500 text-sm mb-3">
//                     You can add children below 17 years old
//                   </p>
//                   <div className="grid grid-cols-2 gap-3">
//                     {childrenAges.map((age, index) => (
//                       <div key={index} className="flex flex-col">
//                         <label className="text-sm text-gray-700 mb-1">
//                           Child {index + 1} Age
//                         </label>
//                         <select
//                           value={age}
//                           onChange={(e) =>
//                             handleAgeChange(index, e.target.value)
//                           }
//                           className="border border-gray-300 rounded-md p-2"
//                         >
//                           {Array.from({ length: 17 }, (_, i) => (
//                             <option key={i + 1} value={i + 1}>
//                               {i + 1} {i === 0 ? "year" : "years"} old
//                             </option>
//                           ))}
//                         </select>
//                       </div>
//                     ))}
//                   </div>
//                 </div>
//               )}

//               <button
//                 className="w-full bg-black text-white px-4 py-2 mt-3 rounded-md font-poppins hover:text-white transition"
//                 onClick={() => setShowTravellersDropdown(false)}
//               >
//                 Done
//               </button>
//             </div>
//           )}
//         </div>

//         {/* Special Rate Section */}
//         <div
//           ref={specialRateRef}
//           className="flex flex-col border border-gray-800 p-3 rounded-md relative cursor-pointer w-1/5"
//           onClick={() => {
//             setShowSpecialRateDropdown(!showSpecialRateDropdown);
//             setShowCalendar(false);
//             setShowTravellersDropdown(false);
//             setShowRoomsDropdown(false);
//           }}
//         >
//           <div
//             className="flex justify-between items-center cursor-pointer"
//             onClick={() => setShowDetails(!showDetails)}
//           >
//             <div className="flex flex-col">
//               <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
//                 Special Rate
//               </label>

//               <div className="flex items-center space-x-2">
//                 <FaPercent className="text-gray-800" />
//                 <span className="text-gray-800 text-base md:text-sm sm:text-xs font-poppins font-semibold">
//                   {getSpecialRateDisplayText()}
//                 </span>
//               </div>
//             </div>

//             <div className="flex items-center">
//               {showDetails ? <FaAngleUp /> : <FaAngleDown />}
//             </div>
//           </div>

//           {showSpecialRateDropdown && (
//             <div
//               className="absolute top-20 left-0 bg-white shadow-lg border rounded-md p-4 z-10 w-76"
//               onClick={(e) => e.stopPropagation()}
//             >
//               <div className="mb-4">
//                 <h3 className="text-sm font-bold text-gray-800 mb-3">Select Special Rate</h3>

//                 {/* Senior Citizen Discount - Checkbox */}
//                 <div className="mb-4 p-3 border border-gray-200 rounded-md hover:bg-gray-50">
//                   <div className="flex items-center justify-between">
//                     <div className="flex items-center space-x-3 flex-1">
//                       <MdElderly className="text-blue-600 text-xl" />
//                       <div>
//                         <label className="font-semibold text-gray-700 cursor-pointer text-sm">
//                           Senior Citizen Discount
//                         </label>
//                         <p className="text-xs text-gray-500">
//                           Apply senior citizen discount
//                         </p>
//                       </div>
//                     </div>
//                     <div className="flex items-center space-x-2">
//                       <input
//                         type="checkbox"
//                         checked={localSeniorDiscount}
//                         onChange={handleSeniorCitizenToggle}
//                         className="h-5 w-5 text-blue-600 rounded cursor-pointer"
//                       />
//                     </div>
//                   </div>
//                 </div>

//                 {/* Corporate Code - Input Field */}
//                 <div className="mb-4 p-3 border border-gray-200 rounded-md hover:bg-gray-50">
//                   <div className="flex items-start space-x-3">
//                     <FaBuilding className="text-green-600 mt-1 text-xl" />
//                     <div className="flex-1">
//                       <label className="font-semibold text-gray-700 mb-1 block text-sm">
//                         Corporate Code
//                       </label>
//                       <div className="flex space-x-2">
//                         <input
//                           type="text"
//                           value={localCorporateCode}
//                           onChange={(e) => {
//                             setLocalCorporateCode(e.target.value);
//                             // Clear validation when user types
//                             if (contractValidation.isValid !== null) {
//                               setContractValidation({
//                                 isValid: null,
//                                 message: "",
//                                 data: null
//                               });
//                             }
//                           }}
//                           placeholder="Enter corporate code"
//                           className={`flex-1 border border-gray-300 rounded-md py-1 px-2 text-sm ${isAnyRateApplied && !corporateCode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
//                           disabled={isValidatingContract || (isAnyRateApplied && !corporateCode)}
//                         />
//                         <button
//                           onClick={handleApplyCorporateCode}
//                           disabled={!localCorporateCode.trim() || isValidatingContract || (isAnyRateApplied && !corporateCode)}
//                           className={`px-4 py-2 rounded-md text-sm transition ${localCorporateCode.trim() && !isValidatingContract && !(isAnyRateApplied && !corporateCode)
//                             ? "bg-black text-white hover:bg-gray-900"
//                             : "bg-gray-300 text-gray-500 cursor-not-allowed"
//                             }`}
//                         >
//                           {isValidatingContract ? "Validating..." : "Apply"}
//                         </button>
//                       </div>
//                       <p className="text-xs text-gray-500 mt-1">
//                         Enter your company code for corporate rates
//                       </p>

//                       {/* Validation status indicator */}
//                       {contractValidation.isValid !== null && (
//                         <div className="mt-2 text-xs">
//                           {contractValidation.isValid === true ? (
//                             <span className="text-green-600 flex items-center">
//                               <FaCheck className="mr-1" /> Contract code is valid
//                             </span>
//                           ) : contractValidation.isValid === false ? (
//                             <span className="text-red-600 flex items-center">
//                               <FaTimes className="mr-1" /> Invalid contract code
//                             </span>
//                           ) : null}
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 </div>

//                 {/* Promo Code - Commented out as requested */}
//                 {/*
//                 <div className="mb-4 p-3 border border-gray-200 rounded-md hover:bg-gray-50">
//                   <div className="flex items-start space-x-3">
//                     <span className="text-xl mt-1">🎟️</span>
//                     <div className="flex-1">
//                       <label className="font-semibold text-gray-700 mb-1 block text-sm">
//                         Promo Code
//                       </label>
//                       <div className="flex space-x-2">
//                         <input
//                           type="text"
//                           value={localPromoCode}
//                           onChange={(e) => setLocalPromoCode(e.target.value)}
//                           placeholder="Enter promo code"
//                           className="flex-1 border border-gray-300 rounded-md py-1 px-2 text-sm"
//                           disabled={isAnyRateApplied && !promoCode}
//                         />
//                         <button
//                           onClick={handleApplyPromoCode}
//                           disabled={!localPromoCode.trim() || (isAnyRateApplied && !promoCode)}
//                           className={`px-4 py-2 rounded-md text-sm transition ${localPromoCode.trim() && !(isAnyRateApplied && !promoCode)
//                             ? "bg-black text-white hover:bg-gray-900"
//                             : "bg-gray-300 text-gray-500 cursor-not-allowed"
//                             }`}
//                         >
//                           Apply
//                         </button>
//                       </div>
//                       <p className="text-xs text-gray-500 mt-1">
//                         Enter promo code for special offers
//                       </p>
//                     </div>
//                   </div>
//                 </div>
//                 */}

//                 {/* Clear Rate Button - Only show if a rate is applied */}
//                 {isAnyRateApplied && (
//                   <div className="mt-4 pt-3 border-t border-gray-200">
//                     <button
//                       onClick={handleClearSpecialRates}
//                       className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm hover:bg-gray-300 transition"
//                     >
//                       Clear Special Rate
//                     </button>
//                   </div>
//                 )}

//                 <button
//                   className="w-full bg-black text-white px-4 py-2 mt-2 rounded-md text-sm font-poppins hover:bg-gray-800 transition"
//                   onClick={() => setShowSpecialRateDropdown(false)}
//                 >
//                   Done
//                 </button>
//               </div>
//             </div>
//           )}
//         </div>

//         {/* Room Selection */}
//         <div
//           ref={roomsRef}
//           className="flex flex-col border border-gray-800 p-3 rounded-md relative cursor-pointer w-1/5"
//           onClick={() => {
//             setShowRoomsDropdown(!showRoomsDropdown);
//             setShowCalendar(false);
//             setShowTravellersDropdown(false);
//             setShowSpecialRateDropdown(false);
//           }}
//         >
//           <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
//             Rooms
//           </label>
//           <div className="flex items-center space-x-2">
//             <MdBedroomParent size={24} className="text-gray-800" />
//             <span className="text-gray-800 text-base md:text-sm sm:text-xs font-poppins font-semibold">
//               {rooms} Room(s)
//             </span>
//           </div>
//           {showRoomsDropdown && (
//             <div className="absolute top-20 left-0 bg-white shadow-lg border rounded-md p-4 z-10 w-76">
//               <div className="flex justify-between items-center py-2">
//                 <span className="text-black font-poppins capitalize font-semibold">
//                   Rooms
//                 </span>
//                 <div className="flex items-center space-x-4">
//                   <button
//                     className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
//                     onClick={(e) =>
//                       handleCounterChange(e, "rooms", Math.max(1, rooms - 1))
//                     }
//                   >
//                     -
//                   </button>
//                   <span className="text-lg font-semibold">{rooms}</span>
//                   <button
//                     className={`px-3.5 py-2 rounded-full transition-colors ${rooms >= 1
//                       ? "bg-gray-200 text-gray-500 cursor-not-allowed"
//                       : "bg-blue-100 text-blue-700 hover:bg-blue-200"
//                       }`}
//                     onClick={(e) => {
//                       if (rooms < 1) {
//                         handleCounterChange(e, "rooms", rooms + 1);
//                       }
//                     }}
//                     disabled={rooms >= 1}
//                   >
//                     +
//                   </button>
//                 </div>
//               </div>

//               <p className="text-xs text-gray-900 mt-2 bg-yellow-200 px-2 py-1 rounded-sm">
//                 Reservations are limited to one room each. To reserve multiple rooms, please create separate reservations
//               </p>
//               <button
//                 className="w-full bg-black text-white px-4 py-2 mt-3 font-poppins rounded-md hover:text-white transition"
//                 onClick={() => setShowRoomsDropdown(false)}
//               >
//                 Done
//               </button>
//             </div>
//           )}
//         </div>
//       </div>

//     </div>
//   );
// };

// export default RoomReserve;
